import { useMemo } from 'react'
import useSWR from 'swr'
import { taxonomyApi, type TaxonomyNodeDto } from '@/lib/api'

export interface IndustryTokenMeta {
  name: string
  tier: number
}

interface AudienceWithIndustryLabels {
  industryLabels?: Array<{ token: string; name: string; tier: number }> | null
}

/** Build token → display meta from list payloads and tier-1 taxonomy browse. */
export function buildBaseIndustryTokenMeta(
  audiences: Array<AudienceWithIndustryLabels>,
  tier1Nodes: Array<TaxonomyNodeDto> | undefined,
  seedNames: Record<string, string> = {},
): Map<string, IndustryTokenMeta> {
  const map = new Map<string, IndustryTokenMeta>()
  for (const node of tier1Nodes ?? []) {
    map.set(node.token, { name: node.name, tier: 1 })
  }
  for (const audience of audiences) {
    for (const label of audience.industryLabels ?? []) {
      map.set(label.token, { name: label.name, tier: label.tier })
    }
  }
  for (const [token, name] of Object.entries(seedNames)) {
    if (token && name && !map.has(token)) {
      map.set(token, { name, tier: 0 })
    }
  }
  return map
}

const RESOLVE_BATCH_SIZE = 50

export async function resolveIndustryTokenMeta(
  tokens: Array<string>,
): Promise<Array<[string, IndustryTokenMeta]>> {
  if (tokens.length === 0) {
    return []
  }
  const entries: Array<[string, IndustryTokenMeta]> = []
  for (let i = 0; i < tokens.length; i += RESOLVE_BATCH_SIZE) {
    const batch = tokens.slice(i, i + RESOLVE_BATCH_SIZE)
    const { nodes } = await taxonomyApi.resolveNodes('industry', batch)
    for (const node of nodes) {
      entries.push([node.token, { name: node.name, tier: node.tier }])
    }
  }
  return entries
}

/**
 * Resolves industry taxonomy tokens to human-readable names for filter chips.
 * Tier-2/3 tokens are UUIDs; names come from audience labels, filter-modal cache,
 * session storage, or POST /taxonomy/industry/nodes/resolve.
 */
export function useIndustryTokenMeta(
  audiences: Array<AudienceWithIndustryLabels>,
  tier1Nodes: Array<TaxonomyNodeDto> | undefined,
  tokensToResolve: Array<string>,
  seedNames: Record<string, string> = {},
): Map<string, IndustryTokenMeta> {
  const baseMeta = useMemo(
    () => buildBaseIndustryTokenMeta(audiences, tier1Nodes, seedNames),
    [audiences, tier1Nodes, seedNames],
  )

  const missingTokens = useMemo(() => {
    const missing: Array<string> = []
    for (const token of tokensToResolve) {
      if (token && !baseMeta.has(token)) {
        missing.push(token)
      }
    }
    return [...new Set(missing)].sort()
  }, [tokensToResolve, baseMeta])

  const swrKey =
    missingTokens.length > 0
      ? (['industry-token-meta', missingTokens] as const)
      : null

  const { data: resolvedEntries } = useSWR(
    swrKey,
    ([, tokens]) => resolveIndustryTokenMeta(tokens),
    { revalidateOnFocus: false, dedupingInterval: 60_000 },
  )

  return useMemo(() => {
    const map = new Map(baseMeta)
    for (const [token, meta] of resolvedEntries ?? []) {
      if (!map.has(token)) {
        map.set(token, meta)
      }
    }
    return map
  }, [baseMeta, resolvedEntries])
}
